import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Chip,
  Button,
  Avatar,
  AvatarGroup,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as LinkIcon,
  ShoppingBag as ClaimIcon,
  RemoveShoppingCart as UnclaimIcon,
  StickyNote2 as NoteIcon,
  AttachMoney as CashIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme';
import {
  isMultiMode,
  isCashItem,
  getRequestedQty,
  getClaims,
  getClaimedSum,
  getMyClaim,
  isFullyClaimed,
} from '../utils/itemClaims';

// Display order for cash-item payment providers. Single-provider items open
// directly; multi-provider items show a menu in this order.
const PROVIDER_PRIORITY = ['venmo', 'paypal', 'cashapp'];
const PROVIDER_LABELS = { venmo: 'Venmo', paypal: 'PayPal', cashapp: 'Cash App' };

function WishlistItem({ item, onEdit, onDelete, isSharedList, onClaim, onLinkClick }) {
  const { user } = useAuth();
  const cash = isCashItem(item);
  const multi = isMultiMode(item);
  const claims = getClaims(item);
  const myClaim = getMyClaim(item, user?._id);

  if (cash) {
    return (
      <CashItem
        item={item}
        user={user}
        claims={claims}
        myClaim={myClaim}
        isSharedList={isSharedList}
        onEdit={onEdit}
        onDelete={onDelete}
        onClaim={onClaim}
      />
    );
  }
  const myClaimQty = myClaim ? (myClaim.quantity || 1) : 0;
  const requestedQty = getRequestedQty(item);
  const claimedSum = getClaimedSum(item);
  const fullyClaimed = isFullyClaimed(item);

  // Single-mode legacy helpers (preserve existing behavior on items with no quantity)
  const legacyClaimer = !multi ? item.status?.claimedBy : null;
  const isClaimedByMe = multi
    ? !!myClaim
    : !!(legacyClaimer && user && legacyClaimer._id === user._id);
  const isClaimedByOther = multi
    ? false  // multi-mode never hides the button outright; it adapts via state
    : !!(legacyClaimer && !isClaimedByMe);
  const isClaimed = multi ? fullyClaimed : (isClaimedByMe || isClaimedByOther);
  // Only fully-claimed multi-items dim like single claimed items do.
  const showClaimedStyle = isSharedList && isClaimed;

  // On shared lists, the parent owns the "warn once per visit, then just
  // open" logic (it's the only thing that knows about every other item's
  // clicks). The owner's own list never gets a handler, so it always opens
  // directly.
  const openLink = (url) => {
    if (isSharedList && onLinkClick) {
      onLinkClick(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const handleCardClick = () => {
    if (isSharedList) {
      if (item.link) {
        openLink(item.link);
      } else if (item.imageUrl) {
        openLink(item.imageUrl);
      }
    } else {
      onEdit && onEdit(item);
    }
  };

  const getPriorityInfo = (priority) => {
    switch (priority) {
      case 1:
        return { label: 'Low', color: colors.text.muted, bg: alpha(colors.text.muted, 0.15) };
      case 2:
        return { label: 'Normal', color: colors.accent, bg: alpha(colors.accent, 0.15) };
      case 3:
        return { label: 'Medium', color: colors.warning, bg: alpha(colors.warning, 0.15) };
      case 4:
        return { label: 'High', color: colors.secondary, bg: alpha(colors.secondary, 0.15) };
      case 5:
        return { label: 'Must Have', color: colors.error, bg: alpha(colors.error, 0.15) };
      default:
        return null;
    }
  };

  const priorityInfo = getPriorityInfo(item.priority);

  return (
    <Card
      sx={{
        position: 'relative',
        cursor: isSharedList
          ? (item.link || item.imageUrl ? 'pointer' : 'default')
          : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        '&:hover': {
          transform: showClaimedStyle ? 'none' : 'translateY(-4px)',
          '& .item-actions': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        ...(showClaimedStyle && {
          opacity: 0.65,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha(colors.background.main, 0.3),
            borderRadius: 'inherit',
            pointerEvents: 'none',
          },
        }),
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              pr: 1,
              color: showClaimedStyle ? colors.text.secondary : colors.text.primary,
            }}
          >
            {item.title}
          </Typography>
          {item.link && (
            <Tooltip title="Open link">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openLink(item.link);
                }}
                sx={{
                  color: colors.primary,
                  backgroundColor: alpha(colors.primary, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(colors.primary, 0.2),
                  },
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Description */}
        {item.description && (
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </Typography>
        )}

        {/* Image */}
        {item.imageUrl && (
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
              backgroundColor: alpha(colors.background.main, 0.5),
            }}
          >
            <CardMedia
              component="img"
              height="160"
              image={item.imageUrl}
              alt={item.title}
              sx={{
                objectFit: 'contain',
                filter: showClaimedStyle ? 'grayscale(0.8)' : 'none',
                transition: 'filter 0.3s ease',
              }}
            />
          </Box>
        )}

        {/* Tags */}
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          {item.price && (
            <Chip
              label={`$${item.price}`}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: alpha(colors.success, 0.15),
                color: colors.success,
                border: `1px solid ${alpha(colors.success, 0.3)}`,
              }}
            />
          )}
          {priorityInfo && (
            <Chip
              label={priorityInfo.label}
              size="small"
              sx={{
                fontWeight: 500,
                backgroundColor: priorityInfo.bg,
                color: priorityInfo.color,
                border: `1px solid ${alpha(priorityInfo.color, 0.3)}`,
              }}
            />
          )}
          {multi && (
            <Chip
              label={isSharedList ? `Qty: ${claimedSum}/${requestedQty} claimed` : `Qty: ${requestedQty}`}
              size="small"
              sx={{
                fontWeight: 500,
                backgroundColor: alpha(colors.primary, 0.15),
                color: colors.primary,
                border: `1px solid ${alpha(colors.primary, 0.3)}`,
              }}
            />
          )}
          {/* Single-mode legacy claimer avatar (unchanged) */}
          {isSharedList && !multi && legacyClaimer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                {isClaimedByMe ? 'You claimed this' : 'Claimed by'}
              </Typography>
              <Tooltip title={legacyClaimer.name || 'Unknown'}>
                <Avatar
                  src={legacyClaimer.picture}
                  alt={legacyClaimer.name}
                  sx={{
                    width: 24,
                    height: 24,
                    border: `2px solid ${isClaimedByMe ? colors.success : colors.text.muted}`,
                    boxShadow: isClaimedByMe ? `0 0 10px ${alpha(colors.success, 0.5)}` : 'none',
                  }}
                >
                  {legacyClaimer.name?.[0] || '?'}
                </Avatar>
              </Tooltip>
            </Box>
          )}
          {/* Multi-mode AvatarGroup */}
          {isSharedList && multi && claims.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
              <AvatarGroup
                max={3}
                sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}
              >
                {claims.map((c, idx) => {
                  const name = c.isAnonymousClaim
                    ? c.anonymousClaimerName
                    : c.claimedBy?.name;
                  const picture = c.isAnonymousClaim ? null : c.claimedBy?.picture;
                  const mine = !!myClaim && !c.isAnonymousClaim &&
                    (c.claimedBy?._id?.toString?.() === user?._id?.toString?.());
                  return (
                    <Tooltip key={idx} title={`${name || 'Unknown'} (${c.quantity || 1})`}>
                      <Avatar
                        src={picture}
                        alt={name}
                        sx={{
                          border: `2px solid ${mine ? colors.success : colors.text.muted}`,
                        }}
                      >
                        {name?.[0] || '?'}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
            </Box>
          )}
        </Box>

        {/* Notes */}
        {item.notes && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(colors.warning, 0.08),
              border: `1px solid ${alpha(colors.warning, 0.2)}`,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <NoteIcon sx={{ color: colors.warning, fontSize: 18, mt: 0.25 }} />
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                fontStyle: 'italic',
                fontSize: '0.85rem',
              }}
            >
              {item.notes}
            </Typography>
          </Box>
        )}

        {/* Action buttons for owner */}
        {!isSharedList && (onEdit || onDelete) && (
          <Box
            className="item-actions"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              opacity: { xs: 1, sm: 0 },
              transform: { xs: 'translateY(0)', sm: 'translateY(8px)' },
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              gap: 0.5,
              backgroundColor: colors.background.elevated,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              padding: '6px',
              boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
            }}
          >
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => onEdit(item)}
                  sx={{
                    color: colors.primary,
                    '&:hover': {
                      backgroundColor: alpha(colors.primary, 0.15),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => onDelete(item)}
                  sx={{
                    color: colors.error,
                    '&:hover': {
                      backgroundColor: alpha(colors.error, 0.15),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Claim button for shared lists. Single-mode hides the button when
            someone else already claimed; multi-mode adapts label and state. */}
        {isSharedList && onClaim && (multi ? true : !isClaimedByOther) && (() => {
          const fullyClaimedNoMine = multi && fullyClaimed && !myClaim;
          let label;
          let icon;
          let palette;  // 'success' | 'error' | 'muted'
          if (multi) {
            if (myClaim) {
              label = `Claimed (${myClaimQty}) — Edit`;
              icon = <UnclaimIcon />;
              palette = 'error';
            } else if (fullyClaimedNoMine) {
              label = 'All claimed';
              icon = <ClaimIcon />;
              palette = 'muted';
            } else {
              label = 'Claim';
              icon = <ClaimIcon />;
              palette = 'success';
            }
          } else {
            label = isClaimedByMe ? 'Unclaim' : 'Claim';
            icon = isClaimedByMe ? <UnclaimIcon /> : <ClaimIcon />;
            palette = isClaimedByMe ? 'error' : 'success';
          }
          const baseColor = palette === 'success'
            ? colors.success
            : palette === 'error'
              ? colors.error
              : colors.text.muted;
          const btnSx = {
            borderRadius: '10px',
            px: 2,
            py: 0.75,
            fontWeight: 600,
            fontSize: '0.8rem',
            background: `linear-gradient(135deg, ${baseColor} 0%, ${alpha(baseColor, 0.8)} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColor} 20%, ${alpha(baseColor, 0.9)} 100%)`,
            },
          };
          const disabled = !!fullyClaimedNoMine;
          const handle = (e) => {
            if (e?.stopPropagation) e.stopPropagation();
            if (disabled) return;
            onClaim(item);
          };
          return (
            <>
              {/* Mobile: inline button */}
              <Box
                sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'flex-end', mt: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={icon}
                  onClick={handle}
                  disabled={disabled}
                  sx={btnSx}
                >
                  {label}
                </Button>
              </Box>
              {/* Desktop: hover button */}
              <Box
                className="item-actions"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  opacity: 0,
                  transform: 'translateY(8px)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={icon}
                  onClick={handle}
                  disabled={disabled}
                  sx={btnSx}
                >
                  {label}
                </Button>
              </Box>
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
}

function CashItem({ item, user, claims, myClaim, isSharedList, onEdit, onDelete, onClaim }) {
  const paymentUrls = item.paymentUrls || {};
  const activeProviders = PROVIDER_PRIORITY.filter(id => paymentUrls[id]);
  const providerLabels = activeProviders.map(id => PROVIDER_LABELS[id]);
  const haveMine = !!myClaim;
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const openProvider = (id) => {
    window.open(paymentUrls[id], '_blank', 'noopener,noreferrer');
  };

  // Card / link click behavior:
  //   - 0 providers: nothing
  //   - 1 provider: open it directly
  //   - 2+ providers: open a menu anchored to the link icon so the sharer
  //     picks which platform to pay through
  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (activeProviders.length === 1) {
      openProvider(activeProviders[0]);
    } else if (activeProviders.length > 1) {
      setMenuAnchor(e.currentTarget);
    }
  };

  const handleCardClick = (e) => {
    if (!isSharedList) {
      onEdit && onEdit(item);
      return;
    }
    if (activeProviders.length === 1) {
      openProvider(activeProviders[0]);
    } else if (activeProviders.length > 1) {
      // Use the link icon as the anchor so the menu reads as belonging to it.
      const linkBtn = e.currentTarget.querySelector('[data-cash-link]');
      setMenuAnchor(linkBtn || e.currentTarget);
    }
  };

  return (
    <Card
      sx={{
        position: 'relative',
        cursor: isSharedList
          ? (activeProviders.length ? 'pointer' : 'default')
          : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        '&:hover': {
          transform: 'translateY(-4px)',
          '& .item-actions': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header — mirror gift card layout with a link indicator top-right */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              pr: 1,
              color: colors.text.primary,
            }}
          >
            {item.title}
          </Typography>
          {activeProviders.length > 0 && (
            <Tooltip
              title={
                activeProviders.length === 1
                  ? `Open ${PROVIDER_LABELS[activeProviders[0]]}`
                  : 'Choose payment platform'
              }
            >
              <IconButton
                size="small"
                data-cash-link
                onClick={handleLinkClick}
                sx={{
                  color: colors.primary,
                  backgroundColor: alpha(colors.primary, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(colors.primary, 0.2),
                  },
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
          >
            {activeProviders.map(id => (
              <MenuItem
                key={id}
                onClick={() => {
                  setMenuAnchor(null);
                  openProvider(id);
                }}
              >
                {PROVIDER_LABELS[id]}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {item.description && (
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </Typography>
        )}

        {/* Tags row — Cash badge + contributor avatar group on the right */}
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <Chip
            icon={<CashIcon sx={{ fontSize: 14, color: `${colors.success} !important` }} />}
            label={providerLabels.length ? `Cash · ${providerLabels.join(', ')}` : 'Cash'}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: alpha(colors.success, 0.15),
              color: colors.success,
              border: `1px solid ${alpha(colors.success, 0.3)}`,
            }}
          />
          {isSharedList && claims.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
              <AvatarGroup
                max={3}
                sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}
              >
                {claims.map((c, idx) => {
                  const name = c.isAnonymousClaim
                    ? c.anonymousClaimerName
                    : c.claimedBy?.name;
                  const picture = c.isAnonymousClaim ? null : c.claimedBy?.picture;
                  const mine = !!myClaim && !c.isAnonymousClaim &&
                    (c.claimedBy?._id?.toString?.() === user?._id?.toString?.());
                  return (
                    <Tooltip key={idx} title={name || 'Unknown'}>
                      <Avatar
                        src={picture}
                        alt={name}
                        sx={{
                          border: `2px solid ${mine ? colors.success : colors.text.muted}`,
                        }}
                      >
                        {name?.[0] || '?'}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
            </Box>
          )}
        </Box>

        {item.notes && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(colors.warning, 0.08),
              border: `1px solid ${alpha(colors.warning, 0.2)}`,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <NoteIcon sx={{ color: colors.warning, fontSize: 18, mt: 0.25 }} />
            <Typography
              variant="body2"
              sx={{ color: colors.text.secondary, fontStyle: 'italic', fontSize: '0.85rem' }}
            >
              {item.notes}
            </Typography>
          </Box>
        )}

        {/* Owner edit/delete buttons (hover-revealed) */}
        {!isSharedList && (onEdit || onDelete) && (
          <Box
            className="item-actions"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              opacity: { xs: 1, sm: 0 },
              transform: { xs: 'translateY(0)', sm: 'translateY(8px)' },
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              gap: 0.5,
              backgroundColor: colors.background.elevated,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              padding: '6px',
              boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
            }}
          >
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(item)} sx={{ color: colors.primary }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(item)} sx={{ color: colors.error }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* "I contributed" toggle — same hover-reveal pattern as the gift claim button */}
        {isSharedList && onClaim && (() => {
          const label = haveMine ? 'Contributed' : 'I contributed';
          const icon = haveMine ? <CheckIcon /> : <CashIcon />;
          const baseColor = haveMine ? colors.error : colors.success;
          const btnSx = {
            borderRadius: '10px',
            px: 2,
            py: 0.75,
            fontWeight: 600,
            fontSize: '0.8rem',
            background: `linear-gradient(135deg, ${baseColor} 0%, ${alpha(baseColor, 0.8)} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColor} 20%, ${alpha(baseColor, 0.9)} 100%)`,
            },
          };
          const handle = (e) => {
            if (e?.stopPropagation) e.stopPropagation();
            onClaim(item);
          };
          return (
            <>
              {/* Mobile: inline button */}
              <Box
                sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'flex-end', mt: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button variant="contained" size="small" startIcon={icon} onClick={handle} sx={btnSx}>
                  {label}
                </Button>
              </Box>
              {/* Desktop: hover button */}
              <Box
                className="item-actions"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  opacity: 0,
                  transform: 'translateY(8px)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Button variant="contained" size="small" startIcon={icon} onClick={handle} sx={btnSx}>
                  {label}
                </Button>
              </Box>
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
}

export default React.memo(WishlistItem);
